// src/components/UI/AlertPills.jsx
//
// Bula de sus (pill-ul dinamic): arată ultimul mesaj din `messages`.
// Mesajul simplu { id, type, text }: clic pe bulă = se închide (ca până acum).
// Mesajul cu `detail` și/sau `actions` are două etape, ca Dynamic Island pe iPhone:
// întâi bula scurtă (`text`); clic pe ea → se deschide (etapa 2) cu explicația și
// butoanele; clic în afară / Escape → se strânge la loc. Alte câmpuri:
//   closable: false — fără ×, nu se poate închide (ex. o eroare cu „Reîncearcă”)
//   onClose — ce se face la închidere în locul lui onDismiss (ex. „închis pentru luna asta”)
//   closeLabel — textul butonului ×
//   actions: [{ label, onClick, primary }] — butoanele din etapa 2
import React, { memo, useEffect, useRef, useState } from "react";
import UIIcon from "../Common/UIIcon";

function normalizePillType(type) {
   const key = String(type || "")
      .trim()
      .toLowerCase();

   if (key === "warning" || key === "warn") return "warning";
   if (
      key === "error" ||
      key === "err" ||
      key === "alert" ||
      key === "danger"
   ) {
      return "error";
   }
   if (key === "success" || key === "ok") return "success";
   return "info";
}

function AlertPills({ messages = [], onDismiss = () => {} }) {
   // Hooks trebuie chemate indiferent de mesaje
   const [leaving, setLeaving] = useState(false);
   const [open, setOpen] = useState(false);
   const pillRef = useRef(null);

   const hasMessage = Array.isArray(messages) && messages.length > 0;
   const m = hasMessage ? messages[messages.length - 1] : null;
   const normalizedType = normalizePillType(m?.type);
   const actions = Array.isArray(m?.actions) ? m.actions : [];
   const expandable = Boolean(m?.detail) || actions.length > 0;
   const closable = m?.closable !== false;

   // când se schimbă mesajul, resetăm starea de ieșire și bula se strânge
   useEffect(() => {
      if (!hasMessage) return;
      setLeaving(false);
      setOpen(false);
   }, [hasMessage, m?.id, m?.text, m?.type]);

   // etapa 2: clic în afară sau Escape → bula se strânge
   useEffect(() => {
      if (!open) return undefined;
      const onDown = (e) => {
         if (pillRef.current && !pillRef.current.contains(e.target)) setOpen(false);
      };
      const onKey = (e) => {
         if (e.key === "Escape") setOpen(false);
      };
      document.addEventListener("pointerdown", onDown, true);
      document.addEventListener("keydown", onKey);
      return () => {
         document.removeEventListener("pointerdown", onDown, true);
         document.removeEventListener("keydown", onKey);
      };
   }, [open]);

   const handleDismiss = () => {
      if (hasMessage && closable && !leaving) setLeaving(true);
   };

   const handleCloseClick = (e) => {
      e.stopPropagation();
      handleDismiss();
   };

   const handlePillClick = () => {
      if (expandable) setOpen((o) => !o);
      else handleDismiss();
   };

   const handleTransitionEnd = (e) => {
      if (!hasMessage) return;
      if (e.target === pillRef.current && leaving) {
         // abia după animație îl scoatem din state
         if (typeof m.onClose === "function") m.onClose();
         else onDismiss();
      }
   };

   // abia acum putem ieși dacă nu e mesaj
   if (!hasMessage) return null;

   const closeButton = closable ? (
      <button
         type="button"
         className="pill__close"
         aria-label={m.closeLabel || "Închide mesajul"}
         title={m.closeLabel || "Închide"}
         onClick={handleCloseClick}
      >
         <UIIcon name="close" className="pill__close-icon" />
      </button>
   ) : null;

   return (
      <div className="pillstack" role="region" aria-label="Mesaje sistem">
         <div
            ref={pillRef}
            className={`pill pill--${normalizedType}${
               expandable ? " pill--expandable" : ""
            }${open ? " pill--open" : ""}${leaving ? " pill--leaving" : ""}`}
            role={normalizedType === "error" ? "alert" : "status"}
            aria-live={normalizedType === "error" ? "assertive" : "polite"}
            aria-expanded={expandable ? open : undefined}
            onClick={handlePillClick}
            onTransitionEnd={handleTransitionEnd}
            tabIndex={0}
            title={
               expandable
                  ? open
                     ? undefined
                     : "Clic pentru detalii"
                  : closable
                    ? "Clic pentru a închide"
                    : undefined
            }
            onKeyDown={(e) => {
               if (e.target !== e.currentTarget) return;
               if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handlePillClick();
               }
            }}
         >
            {expandable ? (
               <>
                  <div className="pill__row">
                     <span className="pill__text">{m.text}</span>
                     <span className="pill__chevron" aria-hidden="true" />
                     {closeButton}
                  </div>
                  {open ? (
                     <div className="pill__more">
                        {m.detail ? (
                           <p className="pill__detail">{m.detail}</p>
                        ) : null}
                        {actions.length ? (
                           <div className="pill__actions">
                              {actions.map((a) => (
                                 <button
                                    key={a.label}
                                    type="button"
                                    className={`pill__action${
                                       a.primary ? " pill__action--primary" : ""
                                    }`}
                                    onClick={(e) => {
                                       e.stopPropagation();
                                       setOpen(false);
                                       a.onClick?.();
                                    }}
                                 >
                                    {a.label}
                                 </button>
                              ))}
                           </div>
                        ) : null}
                     </div>
                  ) : null}
               </>
            ) : (
               <>
                  <span className="pill__text">{m.text}</span>
                  {closeButton}
               </>
            )}
         </div>
      </div>
   );
}

export default memo(AlertPills);
